(() => {
  const STORAGE_KEY = 'ladyboss:boss-list:v1';
  const root = document.querySelector('[data-lb-boss-list]');
  const dock = document.querySelector('[data-lb-boss-open]');

  if (!root || !dock || root.dataset.lbBossReady === 'true') return;
  root.dataset.lbBossReady = 'true';

  const itemsRoot = root.querySelector('[data-lb-boss-items]');
  const emptyState = root.querySelector('[data-lb-boss-empty]');
  const shareButton = root.querySelector('[data-lb-boss-share]');
  const status = document.querySelector('[data-lb-boss-status]');
  const count = dock.querySelector('[data-lb-boss-count]');
  const productCache = new Map();
  let items = readStorage();
  let lastOpener = null;
  let syncQueued = false;

  function sanitizeItem(item) {
    if (!item || typeof item !== 'object') return null;
    const handle = String(item.handle || '').trim().toLowerCase();
    const variantId = String(item.variantId || '').replace(/\D/g, '');
    const title = String(item.title || '').trim().slice(0, 120);
    const color = String(item.color || '').trim().slice(0, 120);
    const url = safeProductUrl(item.url);
    const image = safeImageUrl(item.image);
    const price = Number(item.price);
    const currency = String(item.currency || window.Shopify?.currency?.active || 'USD').slice(0, 8);
    if (!handle || !variantId || !title || !url) return null;
    return { handle, variantId, title, color, url, image, price: Number.isFinite(price) ? price : 0, currency };
  }

  function readStorage() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) return [];
      return stored.map(sanitizeItem).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  function writeStorage() {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      // The in-memory list still works when storage is unavailable.
    }
  }

  function safeProductUrl(value) {
    try {
      const source = new URL(String(value || ''), window.location.origin);
      if (source.origin !== window.location.origin || !source.pathname.includes('/products/')) return '';
      const clean = new URL(source.pathname, window.location.origin);
      ['variant', 'lens'].forEach((name) => {
        const value = source.searchParams.get(name);
        if (value) clean.searchParams.set(name, value);
      });
      return `${clean.pathname}${clean.search}`;
    } catch (error) {
      return '';
    }
  }

  function safeImageUrl(value) {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      if (url.protocol === 'https:') return url.href;
      if (url.origin === window.location.origin && url.protocol === window.location.protocol) return url.href;
      return '';
    } catch (error) {
      return '';
    }
  }

  function exactVariantUrl(value, variantId) {
    const safe = safeProductUrl(value);
    const cleanVariantId = String(variantId || '').replace(/\D/g, '');
    if (!safe || !cleanVariantId) return safe;
    const url = new URL(safe, window.location.origin);
    url.searchParams.set('variant', cleanVariantId);
    return safeProductUrl(url.href);
  }

  function itemKey(item) {
    return `${item.handle}:${item.variantId}`;
  }

  function findStored(item) {
    const key = itemKey(item);
    return items.findIndex((stored) => itemKey(stored) === key);
  }

  function formatPrice(item) {
    if (!item.price) return '';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: item.currency || 'USD',
        maximumFractionDigits: item.price % 100 === 0 ? 0 : 2,
      }).format(item.price / 100);
    } catch (error) {
      return `$${(item.price / 100).toFixed(2)}`;
    }
  }

  function productUrlForPdp(button) {
    try {
      const source = new URL(button.dataset.lbBossUrl || window.location.href, window.location.origin);
      const current = new URL(window.location.href);
      const lens = current.pathname.includes('/products/') ? current.searchParams.get('lens') : '';
      if (lens && !source.searchParams.has('lens')) source.searchParams.set('lens', lens);
      return safeProductUrl(source.href);
    } catch (error) {
      return '';
    }
  }

  function currentCardUrl(card, button) {
    const links = Array.from(card.querySelectorAll('a[href*="/products/"]'));
    const linked = links.map((link) => safeProductUrl(link.href)).find(Boolean);
    return linked || safeProductUrl(button.dataset.lbBossUrl);
  }

  function currentCardState(card, button) {
    const url = currentCardUrl(card, button);
    if (!url) return null;
    const urlObject = new URL(url, window.location.origin);
    const checked = card.querySelector('.product-card__info [type="radio"]:checked[data-variant-id]');
    const checkedId = String(checked?.dataset.variantId || '').replace(/\D/g, '');
    const linkedId = String(urlObject.searchParams.get('variant') || '').replace(/\D/g, '');
    const committedId = String(linkedId || card.dataset.lbCardVariantId || button.dataset.lbBossVariantId || '').replace(/\D/g, '');
    const handleMatch = urlObject.pathname.match(/\/products\/([^/?#]+)/);
    const handle = handleMatch ? decodeURIComponent(handleMatch[1]).toLowerCase() : button.dataset.lbBossHandle;

    // A living card commits its URL and data attribute only after the replacement image is ready.
    if (checkedId && committedId && checkedId !== committedId) return null;
    return { url, variantId: committedId || checkedId, handle };
  }

  function currentCardImage(card, button) {
    const checked = card.querySelector('.product-card__info [type="radio"]:checked');
    if (checked?.dataset.lbCutoutSrc) return safeImageUrl(checked.dataset.lbCutoutSrc);
    if (checked?.dataset.variantMedia) {
      try {
        const media = JSON.parse(checked.dataset.variantMedia);
        const candidate = media.preview_image?.src || media.src || '';
        if (candidate) return safeImageUrl(candidate);
      } catch (error) {
        // Fall through to the committed initial image.
      }
    }
    return safeImageUrl(button.dataset.lbBossImage || card.querySelector('.product-card__image--primary')?.currentSrc || card.querySelector('.product-card__image--primary')?.src);
  }

  function readButtonItem(button) {
    const card = button.closest('product-card');
    if (!card && button.closest('.product.lb-variant-updating')) return null;
    const cardState = card ? currentCardState(card, button) : null;
    if (card && !cardState) return null;
    let url = card ? cardState.url : productUrlForPdp(button);
    const variantId = card ? cardState.variantId : String(button.dataset.lbBossVariantId || new URL(window.location.href).searchParams.get('variant') || '').replace(/\D/g, '');
    url = exactVariantUrl(url, variantId);
    const color = card?.querySelector('[data-lb-selected-color]')?.textContent?.trim() || button.dataset.lbBossColor || '';
    const title = card?.querySelector('.product-title')?.textContent?.trim() || button.dataset.lbBossTitle || '';
    const image = card ? currentCardImage(card, button) : safeImageUrl(button.dataset.lbBossImage);
    return sanitizeItem({
      handle: card ? cardState.handle : button.dataset.lbBossHandle,
      variantId,
      title,
      color,
      url,
      image,
      price: button.dataset.lbBossPrice,
      currency: window.Shopify?.currency?.active || 'USD',
    });
  }

  async function loadProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const request = fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((response) => {
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
      return response.json();
    }).catch((error) => {
      productCache.delete(handle);
      throw error;
    });
    productCache.set(handle, request);
    return request;
  }

  async function resolveItem(button) {
    const item = readButtonItem(button);
    if (!item) return null;

    try {
      const product = await loadProduct(item.handle);
      const variant = product?.variants?.find((candidate) => String(candidate.id) === item.variantId);
      if (!variant) return item;
      const media = variant.featured_image?.src || variant.featured_image || product.featured_image || item.image;
      return sanitizeItem({
        ...item,
        title: String(product.title || item.title).replace(/^LadyBoss\s+/i, '').replace(/™/g, '').trim(),
        image: item.image || media,
        price: variant.price ?? item.price,
      });
    } catch (error) {
      return item;
    }
  }

  function updateButton(button) {
    const item = readButtonItem(button);
    if (!item) return;
    const saved = findStored(item) !== -1;
    const colorText = item.color ? ` in ${item.color}` : '';
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${item.title}${colorText} ${saved ? 'from' : 'to'} Your List`);
    const label = button.querySelector('[data-lb-boss-save-label]');
    if (label) label.textContent = saved ? 'Saved to Your List' : 'Save to Your List';
  }

  function queueButtonSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(() => {
      syncQueued = false;
      document.querySelectorAll('[data-lb-boss-save]').forEach(updateButton);
    });
  }

  function announce(message) {
    if (!status) return;
    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  function renderItem(item) {
    const article = document.createElement('article');
    article.className = 'lb-boss-list__item';
    article.setAttribute('role', 'listitem');
    article.dataset.lbBossItemKey = itemKey(item);

    const imageLink = document.createElement('a');
    imageLink.className = 'lb-boss-list__image-link';
    imageLink.href = item.url;
    imageLink.setAttribute('aria-label', `View ${item.title}${item.color ? ` in ${item.color}` : ''}`);

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = `${item.title}${item.color ? ` in ${item.color}` : ''}`;
      image.loading = 'lazy';
      image.decoding = 'async';
      imageLink.append(image);
    }

    const copy = document.createElement('div');
    copy.className = 'lb-boss-list__item-copy';
    const title = document.createElement('p');
    title.className = 'lb-boss-list__item-title';
    title.textContent = item.title;
    copy.append(title);

    if (item.color) {
      const color = document.createElement('p');
      color.className = 'lb-boss-list__item-color';
      color.textContent = `Color: ${item.color}`;
      copy.append(color);
    }

    const priceText = formatPrice(item);
    if (priceText) {
      const price = document.createElement('p');
      price.className = 'lb-boss-list__item-price';
      price.textContent = priceText;
      copy.append(price);
    }

    const actions = document.createElement('div');
    actions.className = 'lb-boss-list__item-actions';
    const view = document.createElement('a');
    view.className = 'button lb-boss-list__view';
    view.href = item.url;
    view.textContent = 'View pair';
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'button button--secondary lb-boss-list__remove';
    remove.dataset.lbBossRemove = itemKey(item);
    remove.textContent = 'Remove';
    remove.setAttribute('aria-label', `Remove ${item.title}${item.color ? ` in ${item.color}` : ''} from Your List`);
    actions.append(view, remove);
    article.append(imageLink, copy, actions);
    return article;
  }

  function render() {
    itemsRoot.replaceChildren(...items.map(renderItem));
    emptyState.hidden = items.length > 0;
    itemsRoot.hidden = items.length === 0;
    shareButton.disabled = items.length === 0;
    dock.hidden = items.length === 0;
    count.textContent = String(items.length);
    count.setAttribute('aria-label', `${items.length} saved`);
    writeStorage();
    queueButtonSync();
    document.dispatchEvent(new CustomEvent('ladyboss:boss-list-change', { detail: { count: items.length } }));
  }

  async function openList(opener = null) {
    if (opener) lastOpener = opener;
    render();
    await window.customElements.whenDefined('x-drawer');
    if (typeof root.show === 'function') root.show();
  }

  async function toggleSave(button) {
    button.disabled = true;
    const item = await resolveItem(button);
    button.disabled = false;
    const currentItem = button.isConnected ? readButtonItem(button) : null;

    if (!item || !currentItem || itemKey(item) !== itemKey(currentItem) || item.url !== currentItem.url || item.color !== currentItem.color) {
      announce('Give that color a second to load, then save it.');
      return;
    }

    const storedIndex = findStored(item);
    if (storedIndex !== -1) {
      items.splice(storedIndex, 1);
      announce(`${item.title} removed from Your List.`);
      render();
      return;
    }

    items.push(item);
    announce(`${item.title} saved to Your List.`);
    render();
  }

  async function shareList() {
    if (!items.length) return;
    const text = ['Your List', '', ...items.flatMap((item) => [`${item.title}${item.color ? ` — ${item.color}` : ''}`, new URL(item.url, window.location.origin).href, ''])].join('\n').trim();
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Your List', text });
        announce('Your List shared.');
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        announce('Your List copied.');
      } else {
        throw new Error('Sharing is unavailable.');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') announce('Sharing is unavailable in this browser.');
    }
  }

  function canReceiveFocus(element) {
    if (!element?.isConnected || element.disabled || element.hidden) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-lb-boss-open]');
    if (opener) lastOpener = opener;
  }, true);

  document.addEventListener('click', (event) => {
    const saveButton = event.target.closest('[data-lb-boss-save]');
    if (saveButton) {
      event.preventDefault();
      event.stopPropagation();
      toggleSave(saveButton);
      return;
    }

    const openButton = event.target.closest('[data-lb-boss-open]');
    if (openButton) {
      render();
      return;
    }

    const removeButton = event.target.closest('[data-lb-boss-remove]');
    if (removeButton) {
      const index = items.findIndex((item) => itemKey(item) === removeButton.dataset.lbBossRemove);
      if (index !== -1) {
        const [removed] = items.splice(index, 1);
        announce(`${removed.title} removed from Your List.`);
        render();
        const remainingRemoveButtons = itemsRoot.querySelectorAll('[data-lb-boss-remove]');
        const nextButton = remainingRemoveButtons[Math.min(index, remainingRemoveButtons.length - 1)] || root.querySelector('dialog-close-button button');
        nextButton?.focus();
      }
    }
  });

  shareButton.addEventListener('click', shareList);
  root.addEventListener('dialog:before-show', () => {
    if (!lastOpener && document.activeElement instanceof HTMLElement) lastOpener = document.activeElement;
    document.documentElement.classList.add('lb-boss-list-open');
  });
  root.addEventListener('dialog:after-hide', () => {
    document.documentElement.classList.remove('lb-boss-list-open');
    const returnTarget = canReceiveFocus(lastOpener) ? lastOpener : document.querySelector('[data-lb-boss-save]:not([disabled])');
    lastOpener = null;
    window.requestAnimationFrame(() => returnTarget?.focus({ preventScroll: true }));
  });

  document.addEventListener('change', (event) => {
    if (!event.target.closest('product-card, .shopify-section--main-product')) return;
    queueButtonSync();
    window.setTimeout(queueButtonSync, 260);
  });

  const observer = new MutationObserver(queueButtonSync);
  observer.observe(document.querySelector('main') || document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['href', 'data-lb-card-variant-id'],
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    items = readStorage();
    render();
  });

  render();
})();
